import {
  Eye,
  FileText,
  Gauge,
  ListChecks,
  Shield,
  Users,
  type LucideIcon,
} from "lucide-react";

import { cn } from "@/common/utils/utils";

type TypeStyle = { cls: string; icon: LucideIcon };

const TYPE_STYLES: Record<string, TypeStyle> = {
  functional: {
    cls: "bg-brand-50 text-brand-600 border-brand-500/25",
    icon: ListChecks,
  },
  nonfunctional: {
    cls: "bg-surface-sunken text-ink-strong border-ink-line",
    icon: Shield,
  },
  ux: { cls: "bg-warning/10 text-warning border-warning/20", icon: Eye },
  performance: {
    cls: "bg-success/10 text-success border-success/20",
    icon: Gauge,
  },
  customer: {
    cls: "bg-brand-100 text-brand-700 border-brand-500/25",
    icon: Users,
  },
  security: {
    cls: "bg-destructive/10 text-destructive border-destructive/20",
    icon: Shield,
  },
};

const DEFAULT_STYLE: TypeStyle = {
  cls: "bg-surface-sunken text-ink-mid border-ink-line",
  icon: FileText,
};

export const normalizeTypeKey = (raw?: string) =>
  String(raw ?? "")
    .toLowerCase()
    .replace(/[^a-z]/g, "");

export const getTypeStyle = (raw?: string): TypeStyle =>
  TYPE_STYLES[normalizeTypeKey(raw)] ?? DEFAULT_STYLE;

export function ReqTypeBadge({ type }: { type?: string }) {
  const style = getTypeStyle(type);
  const Icon = style.icon;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 h-5 px-1.5 rounded text-[10.5px] font-medium border",
        style.cls,
      )}
    >
      <Icon className="size-3" /> {type || "Unknown"}
    </span>
  );
}

/**
 * Coverage is not a server-provided metric — we represent it honestly as a
 * traced/untraced indicator based on the real `automationcount` (linked tests).
 */
type CoverageInfo = {
  status: string;
  coveragePct: number;
  totalCases: number;
  passed: number;
  failed: number;
  blocked: number;
  notRun: number;
  openDefects: number;
};

const COVERAGE_META: Record<
  string,
  { label: string; bar: string; text: string }
> = {
  PASSED: { label: "Passed", bar: "bg-success", text: "text-success" },
  COVERED: { label: "Covered", bar: "bg-success/70", text: "text-success" },
  PARTIAL: { label: "Partial", bar: "bg-warning", text: "text-warning" },
  FAILED: { label: "Failed", bar: "bg-destructive", text: "text-destructive" },
  BLOCKED: {
    label: "Blocked",
    bar: "bg-destructive/70",
    text: "text-destructive",
  },
  NOT_COVERED: {
    label: "Uncovered",
    bar: "bg-ink-line",
    text: "text-ink-soft",
  },
};

export function CoverageBar({
  tests,
  coverage,
}: {
  tests?: number;
  coverage?: CoverageInfo;
}) {
  // Real derived coverage when available; fall back to the legacy traced/untraced bar.
  if (coverage) {
    const meta = COVERAGE_META[coverage.status] ?? COVERAGE_META.NOT_COVERED;
    const pct =
      coverage.status === "NOT_COVERED"
        ? 0
        : coverage.coveragePct > 0
          ? coverage.coveragePct
          : 100;
    return (
      <div
        className="flex items-center gap-2"
        title={`${coverage.passed}/${coverage.totalCases} passed · ${coverage.openDefects} open defect${coverage.openDefects === 1 ? "" : "s"}`}
      >
        <div className="flex-1 h-1.5 rounded-full bg-surface-sunken overflow-hidden">
          <div
            className={cn("h-full transition-all", meta.bar)}
            style={{ width: `${pct}%` }}
          />
        </div>
        <span
          className={cn(
            "text-[10.5px] tabular-nums w-16 text-right",
            meta.text,
          )}
        >
          {meta.label}
        </span>
      </div>
    );
  }

  const traced = (tests ?? 0) > 0;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 rounded-full bg-surface-sunken overflow-hidden">
        <div
          className={cn(
            "h-full transition-all",
            traced ? "bg-success" : "bg-ink-line",
          )}
          style={{ width: traced ? "100%" : "0%" }}
        />
      </div>
      <span className="text-[10.5px] tabular-nums text-ink-soft w-14 text-right">
        {traced ? "Traced" : "Untraced"}
      </span>
    </div>
  );
}

export function Pulse({
  label,
  value,
  tone,
}: {
  label: string;
  value: string | number;
  tone: "success" | "warning" | "destructive";
}) {
  const c =
    tone === "success"
      ? "text-success"
      : tone === "warning"
        ? "text-warning"
        : "text-destructive";
  return (
    <span className="inline-flex items-baseline gap-1.5">
      <span className={cn("text-[13px] font-semibold tabular-nums", c)}>
        {value}
      </span>
      <span className="uppercase tracking-[0.1em] text-[10px] text-ink-soft">
        {label}
      </span>
    </span>
  );
}

export function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "h-7 px-3 rounded-full text-[11.5px] font-medium transition-all cursor-pointer",
        active
          ? "bg-ink-strong text-white"
          : "bg-surface-raised border border-ink-line text-ink-soft hover:text-ink-strong",
      )}
    >
      {children}
    </button>
  );
}

export const reqTestCount = (row: any): number =>
  Number(row?.automationcount ?? row?.automationCount ?? row?.testCount ?? 0) ||
  0;
